import {
  AllBreakpointsOf,
  BreakpointsOf,
  BreakpointsOfTOrT,
  theme,
} from '../theme';
import { useCurrentBreakpoint } from './useCurrentBreakpoint';

function isBreakpointMap<T>(
  valueOrMap: BreakpointsOfTOrT<T>
): valueOrMap is BreakpointsOf<T> {
  return (
    typeof valueOrMap === 'object' &&
    valueOrMap != null &&
    Object.keys(valueOrMap).length > 0
  );
}

function mergeBreakpointsWithValueMap<
  T,
  V extends BreakpointsOf<T> = BreakpointsOf<T>,
  K extends keyof AllBreakpointsOf<unknown> = keyof AllBreakpointsOf<unknown>
>(valueMap: V, breakpointMap: Required<BreakpointsOf<number>>) {
  let fallbackValue: T;
  const result = {} as Record<K, T>;
  for (const breakpointKey of Object.keys(breakpointMap) as K[]) {
    const currentVal =
      valueMap[breakpointKey] ??
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      fallbackValue!;
    result[breakpointKey] = currentVal;
    fallbackValue = currentVal;
  }
  return result;
}

/**
 * Hook to return the computed value for the current breakpoint if passed
 * a breakpoint-based map of possible values.
 *
 * Replicates bahaviour of the `breakpointMap`
 * function exported from the theme for use in styled-components.
 *
 * @returns value for current breakpoint if given map of values
 * otherwise just returns given value
 */
export function useBreakpointMap<T>(
  valueOrMap: BreakpointsOfTOrT<T>,
  breakpoints = theme.breakpoints
): T {
  const breakpoint = useCurrentBreakpoint(breakpoints);

  if (isBreakpointMap(valueOrMap)) {
    const mergedBreakpointValues = mergeBreakpointsWithValueMap<T>(
      valueOrMap,
      breakpoints
    );

    return mergedBreakpointValues[breakpoint];
  } else return valueOrMap;
}
